/**
 * VersionWatcher — global singleton for client auto-updates.
 *
 * Architecture: ONE watcher started once in main.tsx, lives outside the React
 * router. Polls /manifest-version.json and triggers a hard reload when the
 * server publishes a newer bundle OR when the running bundle is below
 * `minVersion`. Always respects active exam/quiz/practice sessions.
 *
 * Anti-loop, anti-cache and Real-Exam protection are documented inline.
 * Rollback: set VITE_VERSION_WATCHER_DISABLED=1 (start() becomes a no-op).
 */

import { getRunningVersion, isDevBuild, TAB_ID } from "@/lib/version-source";
import { isBelowMinVersion, parseAppVersion } from "@/lib/version-compare";
import {
  safeGetSessionStorage,
  safeRemoveSessionStorage,
  safeSetSessionStorage,
  safeGetLocalStorage,
  safeSetLocalStorage,
  safeRemoveLocalStorage,
  isExamOrPracticeActive,
} from "@/lib/safe-storage";
import { evaluateAutoReload } from "@/lib/auto-reload-policy";

import {
  AUTO_RELOAD_BANNER_GRACE_MS,
  AUTO_RELOAD_RECHECK_MS,
  BACKGROUND_THRESHOLD_MS,
  BACKOFF_STEPS_MS,
  FETCH_TIMEOUT_MS,
  IOS_AUTO_RELOAD_POLL_MS,
  MIN_CHECK_COOLDOWN_MS,
  POLL_INTERVAL_MS,
  RELOAD_TARGET_KEY,
  VERSION_RELOAD_GUARD_KEY,
  VERSION_RELOAD_GUARD_MS,
} from "./constants";
import { detectEnv, isCapacitorNative, isIOSEnv, isPreviewOrIframe } from "./env";
import { _vwGetLastEvent, vwlog } from "./logger";
import { cleanCachesAndReload } from "./cleanup";
import { fetchManifest } from "./manifest";
import { readReloadTarget } from "./reload-target";
import type { ManifestPayload, Subscriber, WatcherState } from "./types";

class VersionWatcher {
  private state: WatcherState;
  private subscribers = new Set<Subscriber>();
  private pollTimer: number | null = null;
  private autoReloadTimer: number | null = null;
  private bannerLoggedForVersion: string | null = null;
  private lastCheckAt = 0;
  private lastVisibleAt = Date.now();
  private started = false;
  private inFlight = false;
  private onServiceWorkerMessage = (event: MessageEvent) => {
    const type = event?.data?.type;
    if (type === "SW_KILL_SWITCH_CLEANED") {
      this.patch({
        reloadTriggeredBy: "service_worker_cleanup",
        lastDecisionReason: "serviceWorkerCleanup:blocked-auto-reload",
        reason: "bannerVisible",
        autoReloadBlocked: true,
        bannerMandatory: true,
      });
    }
  };

  constructor() {
    this.state = {
      watcherStarted: false,
      running: getRunningVersion(),
      serverVersion: null,
      minVersion: null,
      forceFlag: false,
      belowMin: false,
      mismatch: false,
      shouldReload: false,
      examActive: false,
      pendingReload: false,
      lastPollAt: null,
      lastPollOk: false,
      pollCount: 0,
      lastTrigger: null,
      lastManifestUrl: null,
      lastDecisionReason: "init",
      lastFetchError: null,
      reloadTarget: null,
      reloadTargetAge: null,
      swRegistrationsCount: null,
      consecutiveFailures: 0,
      reloadsThisSession: 0,
      disabled: false,
      reason: "init",
      updateDetected: false,
      bannerVisible: false,
      reloadMode: "none",
      autoReloadDisabled: false,
      autoReloadBlocked: false,
      autoReloadBlockReason: null,
      autoReloadScheduledAt: null,
      bannerMandatory: true,
      reloadTriggeredBy: "unknown",
      isIOS: false,
      isIPadOS: false,
      isWebKit: false,
      iosPollerActive: false,
      iosPollerTickCount: 0,
      timerStartedAt: null,
      timerAgeMs: null,
      lastEvent: null,
      reachedFiring: false,
    };
  }

  getState = (): WatcherState => {
    const stored = readReloadTarget();
    const examActive = isExamOrPracticeActive();
    const updateDetected = !!this.state.shouldReload;
    const scheduled = this.autoReloadTimer !== null;
    const reloadMode: WatcherState["reloadMode"] = updateDetected
      ? examActive
        ? "deferred"
        : scheduled
          ? "auto-scheduled"
          : this.state.autoReloadBlocked
            ? "blocked"
            : "manual"
      : "none";
    const reloadTriggeredBy = (() => {
      try {
        const fromQuery = new URLSearchParams(window.location.search).get("updatedBy");
        if (
          fromQuery === "user_click" ||
          fromQuery === "force_refresh" ||
          fromQuery === "service_worker_cleanup" ||
          fromQuery === "boot_gate" ||
          fromQuery === "auto_safe_screen"
        ) {
          return fromQuery;
        }
      } catch {
        /* ignore */
      }
      const stored = safeGetSessionStorage("mblearn_reload_triggered_by");
      if (
        stored === "user_click" ||
        stored === "force_refresh" ||
        stored === "service_worker_cleanup" ||
        stored === "boot_gate" ||
        stored === "auto_safe_screen"
      ) {
        return stored as WatcherState["reloadTriggeredBy"];
      }
      return this.state.reloadTriggeredBy;
    })();
    const env = detectEnv();
    const timerStartedAt = this.state.timerStartedAt;
    const recentReloadAge = this.getRecentReloadAge();
    const landingPath = this.isLandingPath();
    return {
      ...this.state,
      examActive,
      reloadTargetAge: stored ? stored.age : null,
      reloadTarget: stored ? stored.target : this.state.reloadTarget,
      updateDetected,
      bannerVisible: updateDetected && this.state.reloadsThisSession === 0 && recentReloadAge === null && !landingPath,
      reloadMode,
      bannerMandatory: true,
      reloadTriggeredBy,
      isIOS: env.isIOS,
      isIPadOS: env.isIPadOS,
      isWebKit: env.isWebKit,
      iosPollerActive: this.state.iosPollerActive,
      iosPollerTickCount: this.state.iosPollerTickCount,
      timerStartedAt,
      timerAgeMs: timerStartedAt ? Date.now() - timerStartedAt : null,
      lastEvent: _vwGetLastEvent(),
      reachedFiring: this.state.reachedFiring,
    };
  };

  private getRecentReloadAge(): number | null {
    // Use localStorage so the 30-min cooldown survives PWA cold-starts.
    // sessionStorage clears whenever the user fully closes/reopens the app,
    // which caused the "Nueva versión disponible" banner to re-appear on
    // every launch in installed PWAs.
    const raw = safeGetLocalStorage(VERSION_RELOAD_GUARD_KEY);
    if (!raw) return null;
    const ts = Number.parseInt(raw, 10);
    if (!Number.isFinite(ts)) {
      safeRemoveLocalStorage(VERSION_RELOAD_GUARD_KEY);
      return null;
    }
    const age = Date.now() - ts;
    if (age < 0 || age > VERSION_RELOAD_GUARD_MS) {
      safeRemoveLocalStorage(VERSION_RELOAD_GUARD_KEY);
      return null;
    }
    return age;
  }

  private isNativeDisabled(): boolean {
    if (!isCapacitorNative()) return false;
    if (!this.state.disabled || this.state.lastDecisionReason !== "disabled:capacitor-native") {
      this.patch({
        watcherStarted: true,
        disabled: true,
        reason: "disabled",
        lastDecisionReason: "disabled:capacitor-native",
        shouldReload: false,
        pendingReload: false,
        autoReloadBlocked: true,
        autoReloadScheduledAt: null,
        iosPollerActive: false,
      });
    }
    return true;
  }

  private isLandingPath(): boolean {
    try {
      return window.location.pathname === "/";
    } catch {
      return false;
    }
  }

  subscribe = (fn: Subscriber): (() => void) => {
    this.subscribers.add(fn);
    fn(this.getState());
    return () => {
      this.subscribers.delete(fn);
    };
  };

  private emit() {
    const snap = this.getState();
    for (const fn of this.subscribers) {
      try {
        fn(snap);
      } catch (e) {
        console.warn("[VersionWatcher] subscriber threw", e);
      }
    }
  }

  private patch(p: Partial<WatcherState>) {
    this.state = { ...this.state, ...p };
    this.emit();
  }

  start = (): void => {
    if (this.started) return;
    this.started = true;

    // Rollback level 2
    if ((import.meta.env.VITE_VERSION_WATCHER_DISABLED as string) === "1") {
      this.patch({ watcherStarted: true, disabled: true, reason: "disabled", lastDecisionReason: "disabled:env" });
      console.log("[VersionWatcher] disabled via VITE_VERSION_WATCHER_DISABLED=1");
      return;
    }
    if (isDevBuild()) {
      this.patch({ watcherStarted: true, disabled: true, reason: "disabled", lastDecisionReason: "disabled:dev-build" });
      console.log("[VersionWatcher] disabled: dev build");
      return;
    }
    if (this.isNativeDisabled()) {
      this.patch({ watcherStarted: true, disabled: true, reason: "disabled", lastDecisionReason: "disabled:capacitor-native" });
      console.log("[VersionWatcher] disabled: capacitor native");
      return;
    }
    if (isPreviewOrIframe()) {
      this.patch({ watcherStarted: true, disabled: true, reason: "skippedHost", lastDecisionReason: "skippedHost:preview-or-iframe" });
      console.log("[VersionWatcher] disabled: preview/iframe");
      return;
    }

    // Post-reload housekeeping: if running version now matches the target,
    // clear the sentinel (happy path). Expired targets are auto-cleared by
    // readReloadTarget() so the guard can never block indefinitely.
    const stored = readReloadTarget();
    if (stored && stored.target === this.state.running) {
      safeRemoveSessionStorage(RELOAD_TARGET_KEY);
    }
    try {
      const raw = safeGetSessionStorage("mblearn_boot_gate_detected");
      if (raw) {
        const boot = JSON.parse(raw) as {
          mismatch?: boolean;
          belowMin?: boolean;
          forceRefresh?: boolean;
        };
        this.patch({
          reloadTriggeredBy: boot?.forceRefresh ? "force_refresh" : "boot_gate",
          lastDecisionReason: boot?.forceRefresh
            ? "bootGate:forceRefresh-detected"
            : boot?.belowMin
              ? "bootGate:belowMin-detected"
              : boot?.mismatch
                ? "bootGate:mismatch-detected"
                : "bootGate:detected",
          autoReloadBlocked: true,
          bannerMandatory: true,
        });
      }
    } catch {
      /* ignore */
    }

    // One-time cleanup of legacy keys from previous architecture.
    [
      "mblearn_app_version",
      "app-build-version",
      "app-version",
      "mblearn_cache_refresh_in_progress",
      "mblearn_cache_refresh_fallback_done",
      "mblearn_pending_refresh_after_session",
      "mblearn_soft_reload_scheduled",
      "last-update-attempt",
    ].forEach(safeRemoveLocalStorage);

    this.patch({ watcherStarted: true, disabled: false, reason: "started", lastDecisionReason: "started" });
    console.log("[VersionWatcher] started", { running: this.state.running, tab: TAB_ID });

    // Hook DOM events
    window.addEventListener("visibilitychange", this.onVisibility);
    window.addEventListener("focus", this.onFocus);
    window.addEventListener("online", this.onOnline);
    window.addEventListener("pageshow", this.onPageShow);
    navigator.serviceWorker?.addEventListener?.("message", this.onServiceWorkerMessage);

    this.scheduleNext(POLL_INTERVAL_MS);
    // First check shortly after mount
    window.setTimeout(() => {
      if (this.isNativeDisabled()) return;
      void this.check("initial");
    }, 2_000);
  };

  stop = (): void => {
    if (!this.started) return;
    this.started = false;
    if (this.pollTimer !== null) {
      window.clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
    window.removeEventListener("visibilitychange", this.onVisibility);
    window.removeEventListener("focus", this.onFocus);
    window.removeEventListener("online", this.onOnline);
    window.removeEventListener("pageshow", this.onPageShow);
    navigator.serviceWorker?.removeEventListener?.("message", this.onServiceWorkerMessage);
  };

  forceCheck = async (opts: { reason?: string; allowReload?: boolean } = {}): Promise<void> => {
    await this.check(opts.reason || "force", { bypassCooldown: true, allowReload: opts.allowReload !== false });
  };

  /**
   * User-initiated reload from the banner. Clears any stale guard so it cannot
   * be silently blocked, then runs the normal cleanup-and-reload path.
   * Still respects active exam/practice sessions.
   */
  triggerReload = async (reason = "user-banner"): Promise<void> => {
    if (this.isNativeDisabled()) return;
    if (this.state.disabled) return;
    if (isExamOrPracticeActive()) {
      vwlog("auto_refresh_blocked", { source: reason, reason_blocked: "exam_or_practice_active" });
      this.patch({
        reason: "examActive",
        lastDecisionReason: "examActive:user-banner",
        reloadMode: "deferred",
        autoReloadBlocked: true,
        autoReloadBlockReason: "exam_or_practice_active",
      });
      return;
    }
    this.cancelScheduledAutoReload("triggerReload");
    safeRemoveSessionStorage(RELOAD_TARGET_KEY);
    const target = this.state.serverVersion || `min:${this.state.minVersion}` || "user";
    const isAuto = reason.startsWith("auto-");
    const triggeredBy: WatcherState["reloadTriggeredBy"] = isAuto
      ? "auto_safe_screen"
      : this.state.forceFlag
        ? "force_refresh"
        : "user_click";
    if (!isAuto) {
      vwlog("user_clicked_update", {
        running: this.state.running,
        serverVersion: this.state.serverVersion,
        forceFlag: this.state.forceFlag,
      });
    }
    this.patch({
      reloadsThisSession: this.state.reloadsThisSession + 1,
      reason: "reloading",
      lastDecisionReason: `reloading:${reason}`,
      reloadTriggeredBy: triggeredBy,
    });
    await cleanCachesAndReload({
      reason: `version-watcher:${reason}`,
      reloadTarget: target,
      triggeredBy,
    });
  };

  private cancelScheduledAutoReload(reason: string) {
    if (this.autoReloadTimer !== null) {
      // On iOS, ignore visibility-driven cancels — Safari flaps
      // visibilityState during scroll bounce, banner taps, and tab
      // multitasking. Cancelling there is the main reason the auto-reload
      // never fires on iPad. The interval poller's pre-fire safety check
      // already enforces real safety at fire-time.
      if (reason === "page_hidden" && isIOSEnv()) {
        vwlog("auto_refresh_cancel_ignored_ios", { reason });
        return;
      }
      window.clearTimeout(this.autoReloadTimer);
      window.clearInterval(this.autoReloadTimer);
      this.autoReloadTimer = null;
      vwlog("auto_refresh_cancelled", { reason });
      this.patch({ autoReloadScheduledAt: null, iosPollerActive: false, timerStartedAt: null });
    }
  }

  private logPreFireDiagnostics(source: string, extra: Record<string, unknown> = {}) {
    try {
      const el = document.activeElement as HTMLElement | null;
      vwlog("auto_refresh_pre_fire", {
        source,
        visibilityState: document.visibilityState,
        hasFocus: typeof document.hasFocus === "function" ? document.hasFocus() : null,
        activeTag: el?.tagName ?? null,
        activeType: (el as HTMLInputElement | null)?.type ?? null,
        path: window.location.pathname,
        isIOS: isIOSEnv(),
        ...extra,
      });
    } catch {
      /* ignore */
    }
  }

  private fireAutoReload(source: string) {
    if (this.isNativeDisabled()) return;
    this.patch({ reachedFiring: true, iosPollerActive: false });
    vwlog("auto_refresh_firing", {
      source,
      running: this.state.running,
      serverVersion: this.state.serverVersion,
    });
    void this.triggerReload("auto-safe-screen");
    // WebKit/iPadOS fallback: if location.replace inside triggerReload is
    // swallowed/throttled by WebKit, force a hard reload after the next tick.
    const env = detectEnv();
    if (env.useIosPoller) {
      window.setTimeout(() => {
        try {
          vwlog("auto_refresh_webkit_fallback", { source });
          window.location.reload();
        } catch {
          /* ignore */
        }
      }, 1500);
    }
  }

  private scheduleAutoReloadIfSafe(source: string) {
    if (this.isNativeDisabled()) return;
    if (!this.state.shouldReload) return;
    if (this.state.disabled) return;
    const decision = evaluateAutoReload();

    // HARD blocks (exam, critical route, stripe): do not schedule, surface
    // the reason and let the user tap the banner manually.
    if (!decision.safe && decision.severity === "hard") {
      this.cancelScheduledAutoReload("hard_block");
      vwlog("auto_refresh_blocked", { source, reason_blocked: decision.reason, severity: "hard" });
      this.patch({
        autoReloadBlocked: true,
        autoReloadBlockReason: decision.reason,
        reloadMode: "blocked",
      });
      return;
    }

    // SOFT blocks (page hidden, real text input focused): we still want to
    // schedule — the condition usually clears within seconds (user finishes
    // typing, refocuses the tab). Pre-fire re-check enforces safety.
    if (!decision.safe && decision.severity === "soft") {
      vwlog("auto_refresh_deferred_soft", { source, reason_blocked: decision.reason });
    }

    if (this.autoReloadTimer !== null) return; // already scheduled

    const scheduledAt = Date.now();
    const useIOS = isIOSEnv();
    vwlog("auto_refresh_allowed", {
      source,
      delay_ms: AUTO_RELOAD_BANNER_GRACE_MS,
      strategy: useIOS ? "ios_interval_poll" : "single_timeout",
      running: this.state.running,
      serverVersion: this.state.serverVersion,
      softReason: decision.severity === "soft" ? decision.reason : null,
    });
    this.patch({
      autoReloadBlocked: false,
      autoReloadBlockReason: decision.severity === "soft" ? decision.reason : null,
      autoReloadScheduledAt: scheduledAt,
      reloadMode: "auto-scheduled",
      timerStartedAt: scheduledAt,
      iosPollerActive: useIOS,
      iosPollerTickCount: 0,
      reachedFiring: false,
    });

    if (useIOS) {
      // iPad/Safari path: deadline + 2s interval poller. Re-checks safety
      // each tick. Does NOT gate on document.hasFocus() (unreliable on
      // iPadOS). Does NOT use a single setTimeout (gets throttled).
      const deadline = scheduledAt + AUTO_RELOAD_BANNER_GRACE_MS;
      this.autoReloadTimer = window.setInterval(() => {
        const recheck = evaluateAutoReload();
        const now = Date.now();
        this.patch({ iosPollerTickCount: this.state.iosPollerTickCount + 1 });
        this.logPreFireDiagnostics("ios_interval_tick", {
          reason_blocked: recheck.reason,
          severity: recheck.severity,
          timerAgeMs: now - scheduledAt,
          deadlineRemainingMs: deadline - now,
          tickCount: this.state.iosPollerTickCount,
        });
        if (!recheck.safe && recheck.severity === "hard") {
          if (this.autoReloadTimer !== null) {
            window.clearInterval(this.autoReloadTimer);
            this.autoReloadTimer = null;
          }
          vwlog("auto_refresh_blocked", {
            source: "ios_pre_fire",
            reason_blocked: recheck.reason,
            severity: "hard",
          });
          this.patch({
            autoReloadBlocked: true,
            autoReloadBlockReason: recheck.reason,
            autoReloadScheduledAt: null,
            reloadMode: "blocked",
          });
          return;
        }
        // Soft block (e.g. user typing): keep polling until it clears.
        if (!recheck.safe) return;
        if (now < deadline) return;
        if (this.autoReloadTimer !== null) {
          window.clearInterval(this.autoReloadTimer);
          this.autoReloadTimer = null;
        }
        this.fireAutoReload("ios_interval");
      }, IOS_AUTO_RELOAD_POLL_MS) as unknown as number;
      return;
    }

    // Non-iOS path (Android Chrome, desktop): original single-timeout
    // approach — already proven to work; do not change.
    this.autoReloadTimer = window.setTimeout(() => {
      this.autoReloadTimer = null;
      const recheck = evaluateAutoReload();
      this.logPreFireDiagnostics("timeout_pre_fire", {
        reason_blocked: recheck.reason,
        severity: recheck.severity,
      });
      if (!recheck.safe) {
        vwlog("auto_refresh_blocked", {
          source: "pre_fire",
          reason_blocked: recheck.reason,
          severity: recheck.severity,
        });
        this.patch({
          autoReloadBlocked: true,
          autoReloadBlockReason: recheck.reason,
          autoReloadScheduledAt: null,
          reloadMode: recheck.severity === "hard" ? "blocked" : "manual",
        });
        return;
      }
      this.fireAutoReload("timeout");
    }, AUTO_RELOAD_BANNER_GRACE_MS) as unknown as number;
  }

  // ───────── events ─────────
  private onVisibility = () => {
    if (this.isNativeDisabled()) return;
    if (document.visibilityState !== "visible") {
      this.cancelScheduledAutoReload("page_hidden");
      this.patch({ lastTrigger: "visibilitychange", reason: "visibilityHidden", lastDecisionReason: "visibilityHidden" });
      return;
    }
    const away = Date.now() - this.lastVisibleAt;
    this.lastVisibleAt = Date.now();
    void this.check("visibilitychange", { bypassCooldown: away > BACKGROUND_THRESHOLD_MS });
    if (this.state.shouldReload) this.scheduleAutoReloadIfSafe("visibilitychange");
  };
  private onFocus = () => {
    if (this.isNativeDisabled()) return;
    void this.check("focus");
    if (this.state.shouldReload) this.scheduleAutoReloadIfSafe("focus");
  };
  private onOnline = () => {
    if (this.isNativeDisabled()) return;
    void this.check("online", { bypassCooldown: true });
  };
  private onPageShow = (e: PageTransitionEvent) => {
    if (this.isNativeDisabled()) return;
    void this.check(e.persisted ? "pageshow-bfcache" : "pageshow", { bypassCooldown: e.persisted });
  };

  private scheduleNext(delay: number) {
    if (this.isNativeDisabled()) return;
    if (this.pollTimer !== null) window.clearTimeout(this.pollTimer);
    this.pollTimer = window.setTimeout(() => {
      void this.check("interval");
    }, delay) as unknown as number;
  }

  private nextDelay(): number {
    const fails = this.state.consecutiveFailures;
    if (fails <= 0) return POLL_INTERVAL_MS;
    const i = Math.min(fails - 1, BACKOFF_STEPS_MS.length - 1);
    return BACKOFF_STEPS_MS[i];
  }

  private async check(trigger: string, opts: { bypassCooldown?: boolean; allowReload?: boolean } = {}): Promise<void> {
    if (this.isNativeDisabled()) return;
    if (this.state.disabled) {
      this.patch({ lastTrigger: trigger, reason: "disabled", lastDecisionReason: "disabled" });
      return;
    }
    if (this.inFlight) {
      this.patch({ lastTrigger: trigger, reason: "inFlight", lastDecisionReason: "inFlight" });
      return;
    }

    const now = Date.now();
    const recentReloadAge = this.getRecentReloadAge();
    if (recentReloadAge !== null) {
      this.cancelScheduledAutoReload("recent_reload_guard");
    }
    if (!opts.bypassCooldown && now - this.lastCheckAt < MIN_CHECK_COOLDOWN_MS) {
      this.patch({ lastTrigger: trigger, reason: "cooldown", lastDecisionReason: "cooldown" });
      return;
    }
    this.lastCheckAt = now;
    this.inFlight = true;

    let manifest: ManifestPayload | null = null;
    let manifestUrl: string | null = null;
    let ok = false;
    let fetchError: string | null = null;
    try {
      const ctl = new AbortController();
      const t = window.setTimeout(() => ctl.abort(), FETCH_TIMEOUT_MS);
      try {
        const result = await fetchManifest(ctl.signal);
        manifest = result.manifest;
        manifestUrl = result.url;
        ok = true;
      } finally {
        window.clearTimeout(t);
      }
    } catch (e) {
      fetchError = e instanceof Error ? e.message : String(e);
      console.warn("[VersionWatcher] manifest fetch failed", e);
    }

    if (!ok || !manifest) {
      this.patch({
        lastPollAt: now,
        lastPollOk: false,
        pollCount: this.state.pollCount + 1,
        lastTrigger: trigger,
        lastManifestUrl: manifestUrl,
        lastFetchError: fetchError,
        shouldReload: false,
        consecutiveFailures: this.state.consecutiveFailures + 1,
        reason: "fetchFailed",
        lastDecisionReason: "fetchFailed",
      });
      this.inFlight = false;
      this.scheduleNext(this.nextDelay());
      return;
    }

    const running = this.state.running;
    const belowMin = isBelowMinVersion(running, manifest.minVersion);
    const mismatch =
      manifest.version !== null &&
      parseAppVersion(manifest.version) !== null &&
      manifest.version !== running;
    const shouldReload = manifest.forceRefresh || belowMin || mismatch;
    const examActive = isExamOrPracticeActive();

    if (shouldReload && recentReloadAge !== null) {
      this.patch({
        serverVersion: manifest.version,
        minVersion: manifest.minVersion,
        forceFlag: manifest.forceRefresh,
        belowMin,
        mismatch,
        shouldReload: false,
        examActive,
        pendingReload: false,
        lastPollAt: now,
        lastPollOk: true,
        pollCount: this.state.pollCount + 1,
        lastTrigger: trigger,
        lastManifestUrl: manifestUrl,
        lastFetchError: null,
        consecutiveFailures: 0,
        reason: "recentReloadGuard",
        lastDecisionReason: "recentReloadGuard:30min",
        autoReloadBlocked: true,
        autoReloadBlockReason: "recent_reload_guard",
        autoReloadScheduledAt: null,
      });
      this.inFlight = false;
      this.scheduleNext(POLL_INTERVAL_MS);
      return;
    }

    // Best-effort SW registration count for debug
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .getRegistrations()
        .then((regs) => this.patch({ swRegistrationsCount: regs.length }))
        .catch(() => {});
    }

    const wasShouldReload = this.state.shouldReload;
    this.patch({
      serverVersion: manifest.version,
      minVersion: manifest.minVersion,
      forceFlag: manifest.forceRefresh,
      belowMin,
      mismatch,
      shouldReload,
      examActive,
      pendingReload: shouldReload && examActive,
      lastPollAt: now,
      lastPollOk: true,
      pollCount: this.state.pollCount + 1,
      lastTrigger: trigger,
      lastManifestUrl: manifestUrl,
      lastFetchError: null,
      consecutiveFailures: 0,
      reason: shouldReload ? "updateDetected" : "sameVersion",
      lastDecisionReason: shouldReload ? "updateDetected" : "sameVersion",
      bannerMandatory: true,
    });

    this.inFlight = false;

    if (!shouldReload) {
      this.cancelScheduledAutoReload("no_update");
      this.bannerLoggedForVersion = null;
      this.patch({ reason: "sameVersion", lastDecisionReason: "sameVersion" });
      this.scheduleNext(POLL_INTERVAL_MS);
      return;
    }

    // Log update_detected / banner_shown once per server version
    const versionKey = manifest.version || `min:${manifest.minVersion}` || "force";
    if (this.bannerLoggedForVersion !== versionKey || !wasShouldReload) {
      vwlog("update_detected", {
        trigger,
        running,
        serverVersion: manifest.version,
        minVersion: manifest.minVersion,
        forceFlag: manifest.forceRefresh,
        belowMin,
        mismatch,
      });
      vwlog("banner_shown", {
        examActive,
        path: typeof window !== "undefined" ? window.location.pathname : null,
      });
      this.bannerLoggedForVersion = versionKey;
    }

    this.patch({
      reason: examActive ? "examActive" : "bannerVisible",
      lastDecisionReason: examActive ? "bannerDeferred:examActive" : "bannerVisible:awaitingUserTap",
      reloadTriggeredBy: manifest.forceRefresh ? "force_refresh" : "unknown",
    });

    // Try to schedule a safe auto-reload after the banner grace window.
    // The policy re-evaluates exam/critical-route/form/visibility before firing.
    this.scheduleAutoReloadIfSafe(`check:${trigger}`);

    // While a pending update exists, recheck the safe window faster than the
    // normal poll interval so users on a safe screen get the auto-refresh soon
    // after navigating away from a quiz/exam/form.
    this.scheduleNext(AUTO_RELOAD_RECHECK_MS);
  }
}

export const versionWatcher = new VersionWatcher();
